import { useCreateFeedback } from "@/api/feedback/useCreateFeedback";
import { CreateFeedbackRequestDto, UserRatingValue } from "@/api/generated/generatedTypes";
import { useCreateUserRatings } from "@/api/user-rating/useCreateUserRatings";
import { useGetRateableParticipants } from "@/api/user-rating/useGetRateableParticipants";
import { StyledActionButton } from "@/components/shared/StyledActionButton";
import {
  Box,
  Center,
  Group,
  Modal,
  SegmentedControl,
  Stack,
  Text,
  Textarea,
  UnstyledButton,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { IconStar, IconStarFilled } from "@tabler/icons-react";
import { useState } from "react";
import UserRatingSection from "./UserRatingSection";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  eventId: number;
};

function ClickRating({ value, onChange }: { value: number; onChange: (value: number) => void }) {
  return (
    <Group gap={4}>
      {Array.from({ length: 5 }, (_, index) => {
        const starValue = index + 1;
        const isFilled = starValue <= value;

        return (
          <UnstyledButton
            key={starValue}
            type="button"
            aria-label={`${starValue}점`}
            onClick={() => onChange(starValue)}
          >
            {isFilled ? (
              <IconStarFilled size={28} color="var(--mantine-color-yellow-filled)" />
            ) : (
              <IconStar size={28} color="var(--mantine-color-gray-4)" />
            )}
          </UnstyledButton>
        );
      })}
    </Group>
  );
}

function FeedbackBlock({ children }: { children: React.ReactNode }) {
  return (
    <Box
      style={{
        padding: "10px",
        borderRadius: "10px",
        backgroundColor: "#f1f3f5",
        border: "1px solid #dee2e6",
      }}
    >
      <Text ta="center" c="black" fz="sm">
        {children}
      </Text>
    </Box>
  );
}

function FeedbackModal({ isOpen, onClose, eventId }: Props) {
  const createFeedbackMutation = useCreateFeedback();
  const createUserRatingsMutation = useCreateUserRatings();
  const { data: rateableParticipants, isLoading: isLoadingParticipants } =
    useGetRateableParticipants(eventId, isOpen);

  const [selectedRatings, setSelectedRatings] = useState<Record<string, UserRatingValue>>({});

  const form = useForm<Omit<CreateFeedbackRequestDto, "eventId">>({
    initialValues: {
      groupCompatibilityScore: 0,
      restaurantScore: 0,
      afterLocationScore: 0,
      recommendationToFriendScore: 0,
      wouldReAttend: true,
      openFeedback: "",
    },
    validate: {
      groupCompatibilityScore: (value) => (value > 0 ? null : "필수 항목입니다."),
      restaurantScore: (value) => (value > 0 ? null : "필수 항목입니다."),
      afterLocationScore: (value) => (value > 0 ? null : "필수 항목입니다."),
      recommendationToFriendScore: (value) => (value > 0 ? null : "필수 항목입니다."),
    },
  });

  const handleSelectRating = (userId: string, value: UserRatingValue) => {
    setSelectedRatings((previous) => ({ ...previous, [userId]: value }));
  };

  const handleSubmit = async (values: typeof form.values) => {
    const ratings = Object.entries(selectedRatings).map(([ratedUserId, value]) => ({
      ratedUserId,
      value,
    }));

    try {
      // Ratings go first: they tolerate being retried, whereas a submitted feedback
      // closes the form for good and would strand any unsaved ratings.
      if (ratings.length > 0) {
        await createUserRatingsMutation.mutateAsync({ eventId, ratings });
      }

      await createFeedbackMutation.mutateAsync({ ...values, eventId });

      notifications.show({
        title: "감사합니다!",
        message: "소중한 피드백 감사합니다!",
        color: "green",
        autoClose: 4000,
        position: "top-center",
      });
      onClose();
      form.reset();
      setSelectedRatings({});
    } catch {
      notifications.show({
        title: "피드백 등록 실패",
        message: "피드백 등록에 실패했습니다. 잠시 후 다시 시도해주세요.",
        color: "red",
      });
    }
  };

  return (
    <Modal opened={isOpen} onClose={onClose} title="피드백 남기기" centered>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack>
          <FeedbackBlock>
            <Text pb="sm" ta="center">
              오늘 함께한 그룹, 내 취향과 잘 맞았나요?
            </Text>
            <Center mb="sm">
              <ClickRating
                value={form.values.groupCompatibilityScore}
                onChange={(value) => form.setFieldValue("groupCompatibilityScore", value)}
              />
            </Center>
            {form.errors.groupCompatibilityScore && (
              <Text c="red" size="xs" ta="center">
                {form.errors.groupCompatibilityScore}
              </Text>
            )}
          </FeedbackBlock>
          <FeedbackBlock>
            <Text pb="sm" ta="center">
              오늘 방문한 레스토랑 어떠셨나요?
            </Text>
            <Center mb="sm">
              <ClickRating
                value={form.values.restaurantScore}
                onChange={(value) => form.setFieldValue("restaurantScore", value)}
              />
            </Center>
            {form.errors.restaurantScore && (
              <Text c="red" size="xs" ta="center">
                {form.errors.restaurantScore}
              </Text>
            )}
          </FeedbackBlock>
          <FeedbackBlock>
            <Text pb="sm" ta="center">
              애프터 장소는 어떠셨나요?
            </Text>
            <Center mb="sm">
              <ClickRating
                value={form.values.afterLocationScore}
                onChange={(value) => form.setFieldValue("afterLocationScore", value)}
              />
            </Center>
            {form.errors.afterLocationScore && (
              <Text c="red" size="xs" ta="center">
                {form.errors.afterLocationScore}
              </Text>
            )}
          </FeedbackBlock>
          <FeedbackBlock>
            <Text pb="sm" ta="center">
              오프의 경험을 친구에게 추천할 가능성이 얼마나 되나요?
            </Text>
            <Center mb="sm">
              <ClickRating
                value={form.values.recommendationToFriendScore}
                onChange={(value) => form.setFieldValue("recommendationToFriendScore", value)}
              />
            </Center>
            {form.errors.recommendationToFriendScore && (
              <Text c="red" size="xs" ta="center">
                {form.errors.recommendationToFriendScore}
              </Text>
            )}
          </FeedbackBlock>
          <FeedbackBlock>
            <Text pb="sm" ta="center">
              다음 오프닝 테이블 저녁 식사에도 참여할 계획이 있나요?
            </Text>
            <Center>
              <SegmentedControl
                value={form.values.wouldReAttend ? "true" : "false"}
                onChange={(value) => form.setFieldValue("wouldReAttend", value === "true")}
                data={[
                  { label: "네!", value: "true" },
                  { label: "아니오", value: "false" },
                ]}
              />
            </Center>
          </FeedbackBlock>
          {rateableParticipants?.isRatingOpen && (
            <FeedbackBlock>
              <Text ta="center">
                대화가 흥미로웠거나 잘 통했던 분들이 있나요? (다음에 우연히 다시 만나도 좋아요!)
              </Text>
              <Text pb="sm" ta="center" fz="xs" c="dimmed">
                남기신 평가는 상대방에게 공개되지 않으며, 한 번 남기면 수정할 수 없어요.
              </Text>
              <UserRatingSection
                participants={rateableParticipants.participants}
                selectedRatings={selectedRatings}
                onSelect={handleSelectRating}
                isLoading={isLoadingParticipants}
              />
            </FeedbackBlock>
          )}
          <FeedbackBlock>
            <Text pb="sm" ta="center">
              오늘 저녁에 대한 의견이나 피드백이 있다면 자유롭게 남겨주세요!
            </Text>
            <Textarea {...form.getInputProps("openFeedback")} />
          </FeedbackBlock>

          <Center>
            <StyledActionButton
              type="submit"
              disabled={Object.keys(form.errors).length > 0}
              loading={createFeedbackMutation.isPending || createUserRatingsMutation.isPending}
              size="md"
              textColor="black"
              textSize="md"
              fontWeight={500}
              style={{ border: "1px solid black" }}
            >
              등록하기
            </StyledActionButton>
          </Center>
        </Stack>
      </form>
    </Modal>
  );
}

export default FeedbackModal;
